import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    min: {
      type: Number,
    },
    value: {
      state: true,
    },
    max: {
      type: String,
      attribute: 'max',
    },
  }

  constructor() {
    super()

    this.broadcast = this.broadcast.bind(this)
    this.id = Math.random() * 100000
    this.max = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this.value = this.initial
    this.updateContent = this.updateContent.bind(this)
    document.addEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  updateContent(event) {
    const newMax = event.detail.html?.getAttribute('max')
    if (!newMax) return
    this.setAttribute('max', newMax)
    this.max = newMax
    if (this.value > Number(newMax)) {
      this.value = Number(newMax)
      this.broadcast()
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
    }
  }

  broadcast() {
    const $input = this.renderRoot.querySelector('slot[name="input"]')

    if ($input) {
      $input.value = this.value
    }

    this.dispatchEvent(
      new CustomEvent('changeQuantityStarted', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this.value,
        },
      }),
    )
  }

  increment() {
    if (this.max !== '' && this.value >= Number(this.max)) {
      return
    }
    this.value += 1
    this.broadcast()
  }

  decrement() {
    if (!this.value || this.value <= this.min) {
      return
    }

    this.value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Math.max(0, e.target.value)
    if (this.max !== '' && value > Number(this.max)) {
      value = this.max
    }
    this.value = value
    this.broadcast()
  }

  render() {
    return html`
      <span>
        <div class="wrapper" part="wrapper">
          <button @click=${this.decrement} name="decrease" class="decrease" part="decrease">
            ${paths.minus}
          </button>
          <label class="sr-only" for="Quantity-${this.id}">
            ${this.label | 'Quantity'}
          </label>
          <slot name="input"></slot>
          <input
            id="Quantity-${this.id}"
            class="input"
            type="number"
            name="updates[]"
            value=${this.value}
            @change=${this.override}
            min=${this.min}
            max=${this.max}
            part="input"
          />
          <button @click=${this.increment} name="increase" class="increase" part="increase">
            ${paths.plus}
          </button>
        </div>
      </span>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      .wrapper {
        border: 1px solid var(--nova-color-grey-300);
        border-radius: var(--nova-border-radius-s);
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: var(--nova-height);
      }
      .input {
        color: currentColor;
        font: #000;
        text-align: center;
        border: 0;
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;

        &:focus-visible {
          outline: none;
        }
      }
      button {
        height: var(--nova-button-height-s);
        width: calc(var(--nova-space-sm) + var(--nova-space-xs));
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
      }
      .decrease {
        padding: 0 0 0 var(--nova-space-sm);
      }
      .increase {
        padding: 0 var(--nova-space-sm) 0 0;
      }
      button:hover {
        color: #000;
      }
      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }
      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)
